import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import { MOUSE, Raycaster, Vector2, Vector3, type Intersection, type Mesh, type Object3D } from 'three'
import { DISTANCIA_MINIMA } from './comandosCamera'
import { estadoNavegacao, TECLAS_DE_ANDAR } from './estadoNavegacao'

/**
 * Andar pela cena e focar nos detalhes, além da órbita:
 *
 *  - **Toque duplo (ou clique duplo)** num ponto da cena: a câmera chega perto
 *    dele e passa a girar em volta dele. É assim que se olha um mosquetão, uma
 *    polia, o pé da escada; um novo toque duplo noutro lugar troca o foco.
 *  - **Espaço apertado**: arrastar com o botão esquerdo move a cena (a mão do
 *    Photoshop), sem selecionar nem mexer em peça. O botão do meio também move.
 *  - **Botão direito apertado + W A S D** (Q e E descem e sobem): andar pela cena.
 *  - **Girar** em volta do que está sob o cursor, e não de um ponto distante.
 *  - **Setas do teclado**: andam para a frente, para trás e para os lados no
 *    plano do chão, levando junto o ponto em volta do qual se gira; PageUp e
 *    PageDown sobem e descem; com Shift, três vezes mais rápido.
 *
 * O toque duplo é lido direto no elemento do canvas (e não nos eventos das
 * peças), para funcionar sobre qualquer coisa — corda, fita, chão — sem tirar
 * a seleção do primeiro toque.
 */

interface ControlesOrbita {
  target: Vector3
  mouseButtons?: { LEFT?: number; MIDDLE?: number; RIGHT?: number }
  update: () => void
  enabled?: boolean
  addEventListener?: (tipo: string, ouvinte: () => void) => void
  removeEventListener?: (tipo: string, ouvinte: () => void) => void
}

/** Dois toques a menos disto, em ms e em px, são um toque duplo. */
const INTERVALO_TOQUE_DUPLO_MS = 400
const DISTANCIA_TOQUE_DUPLO_PX = 28
/** Um toque que arrastou mais que isto foi órbita, não toque. */
const ARRASTO_MAXIMO_PX = 10
/** Duração da aproximação até o ponto focado. */
const DURACAO_FOCO_MS = 420
/** De quão perto se olha o ponto focado: a menos disto a câmera não se afasta. */
const DISTANCIA_DO_DETALHE_M = 1.1

const suavizar = (t: number) => 1 - Math.pow(1 - t, 3)

/** Malha que de fato se vê: alvos de toque invisíveis (opacidade 0) não contam. */
function visivel(acerto: Intersection): boolean {
  let o: Object3D | null = acerto.object
  while (o) {
    if (!o.visible) return false
    o = o.parent
  }
  const material = (acerto.object as Mesh).material
  const materiais = Array.isArray(material) ? material : [material]
  return materiais.some((m) => m && m.visible !== false && !(m.transparent && m.opacity <= 0.01))
}

/** Para onde a câmera vai ao focar `ponto`: na mesma direção de onde já se olhava, mais perto. */
export function poseDeFoco(camera: Vector3, alvo: Vector3, ponto: Vector3): { posicao: Vector3; alvo: Vector3 } {
  const direcao = camera.clone().sub(alvo)
  const distanciaAtual = camera.distanceTo(ponto)
  if (direcao.lengthSq() < 1e-9) direcao.set(1, 0.6, 1)
  const distancia = Math.max(DISTANCIA_MINIMA * 1.5, Math.min(distanciaAtual, DISTANCIA_DO_DETALHE_M))
  return { posicao: ponto.clone().add(direcao.normalize().multiplyScalar(distancia)), alvo: ponto.clone() }
}

export function ExplorarCena({ velocidadeAndar }: { velocidadeAndar: number }) {
  const camera = useThree((estado) => estado.camera)
  const cena = useThree((estado) => estado.scene)
  const gl = useThree((estado) => estado.gl)
  const controles = useThree((estado) => estado.controls) as ControlesOrbita | null

  const foco = useRef<{ de: { posicao: Vector3; alvo: Vector3 }; para: { posicao: Vector3; alvo: Vector3 }; inicio: number } | null>(null)
  const teclas = useRef(new Set<string>())

  // toque duplo / clique duplo: foca no ponto tocado
  useEffect(() => {
    const elemento = gl.domElement
    let ultimo: { x: number; y: number; t: number } | null = null
    let inicio: { x: number; y: number; id: number } | null = null
    let dedos = 0
    const raio = new Raycaster()

    const focar = (x: number, y: number) => {
      if (!controles) return
      const caixa = elemento.getBoundingClientRect()
      const ndc = new Vector2(((x - caixa.left) / caixa.width) * 2 - 1, -((y - caixa.top) / caixa.height) * 2 + 1)
      raio.setFromCamera(ndc, camera)
      const acerto = raio.intersectObjects(cena.children, true).find(visivel)
      if (!acerto) return
      foco.current = {
        de: { posicao: camera.position.clone(), alvo: controles.target.clone() },
        para: poseDeFoco(camera.position, controles.target, acerto.point),
        inicio: performance.now(),
      }
    }

    // gira em volta do que está sob o cursor: leva o pivô para a mesma
    // profundidade, sobre o eixo da câmera, para a imagem não saltar
    const repivotar = (x: number, y: number) => {
      if (!controles) return
      const caixa = elemento.getBoundingClientRect()
      const ndc = new Vector2(((x - caixa.left) / caixa.width) * 2 - 1, -((y - caixa.top) / caixa.height) * 2 + 1)
      raio.setFromCamera(ndc, camera)
      const acerto = raio.intersectObjects(cena.children, true).find(visivel)
      if (!acerto) return
      const frente = camera.getWorldDirection(new Vector3())
      const profundidade = acerto.point.clone().sub(camera.position).dot(frente)
      if (profundidade < DISTANCIA_MINIMA) return
      controles.target.copy(camera.position).addScaledVector(frente, profundidade)
    }

    const aoBaixar = (e: PointerEvent) => {
      // o foco volta para a cena: sem isso, os atalhos ficam presos no último botão clicado
      const ativo = document.activeElement
      if (ativo instanceof HTMLElement && ativo !== document.body) ativo.blur()
      if (e.pointerType === 'mouse' && e.button === 2) estadoNavegacao.botaoDireito = true
      if (e.pointerType === 'mouse' && e.button === 0 && !estadoNavegacao.espaco) repivotar(e.clientX, e.clientY)
      dedos += 1
      inicio = dedos === 1 ? { x: e.clientX, y: e.clientY, id: e.pointerId } : null
    }
    const aoSoltar = (e: PointerEvent) => {
      if (e.button === 2) estadoNavegacao.botaoDireito = false
      dedos = Math.max(0, dedos - 1)
      const comeco = inicio
      inicio = null
      // só toques curtos com um dedo; arrasto é órbita, dois dedos é pinça
      if (!comeco || comeco.id !== e.pointerId || Math.hypot(e.clientX - comeco.x, e.clientY - comeco.y) > ARRASTO_MAXIMO_PX) {
        ultimo = null
        return
      }
      // a hora do toque, e não a hora em que ele é tratado: com a cena pesada, o tratamento atrasa
      const agora = e.timeStamp
      if (ultimo && agora - ultimo.t < INTERVALO_TOQUE_DUPLO_MS && Math.hypot(e.clientX - ultimo.x, e.clientY - ultimo.y) < DISTANCIA_TOQUE_DUPLO_PX) {
        ultimo = null
        focar(e.clientX, e.clientY)
        return
      }
      ultimo = { x: e.clientX, y: e.clientY, t: agora }
    }
    const aoCancelar = () => {
      dedos = 0
      inicio = null
      estadoNavegacao.botaoDireito = false
    }
    const soltarDireito = (e: PointerEvent) => {
      if (e.button === 2) estadoNavegacao.botaoDireito = false
    }
    window.addEventListener('pointerup', soltarDireito)

    elemento.addEventListener('pointerdown', aoBaixar)
    elemento.addEventListener('pointerup', aoSoltar)
    elemento.addEventListener('pointercancel', aoCancelar)
    return () => {
      elemento.removeEventListener('pointerdown', aoBaixar)
      elemento.removeEventListener('pointerup', aoSoltar)
      elemento.removeEventListener('pointercancel', aoCancelar)
      window.removeEventListener('pointerup', soltarDireito)
    }
  }, [gl, camera, cena, controles])

  // botão do meio também move a cena
  useEffect(() => {
    if (controles?.mouseButtons) controles.mouseButtons.MIDDLE = MOUSE.PAN
  }, [controles])

  // pegar a órbita no meio da aproximação a interrompe
  useEffect(() => {
    if (!controles?.addEventListener) return
    const parar = () => {
      foco.current = null
    }
    controles.addEventListener('start', parar)
    return () => controles.removeEventListener?.('start', parar)
  }, [controles])

  // espaço: arrastar move a cena; setas, PageUp/PageDown e, com o botão direito, W A S D: andar pela cena
  useEffect(() => {
    const ANDAR = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown'])
    const digitando = (alvo: EventTarget | null) =>
      alvo instanceof HTMLElement && (alvo.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo.tagName) || Boolean(alvo.closest('[role="toolbar"], [role="tablist"], [role="slider"]')))
    // o espaço também aperta o botão focado: só vale a mão se o foco não está num controle
    const emControle = (alvo: EventTarget | null) =>
      alvo instanceof HTMLElement && Boolean(alvo.closest('button, a, summary, [role="button"], [role="radio"], [role="checkbox"]'))
    const tecla = (e: KeyboardEvent) => (estadoNavegacao.botaoDireito ? TECLAS_DE_ANDAR[e.key.toLowerCase()] : undefined) ?? e.key
    const maoAtiva = (ativa: boolean) => {
      estadoNavegacao.espaco = ativa
      if (controles?.mouseButtons) controles.mouseButtons.LEFT = ativa ? MOUSE.PAN : MOUSE.ROTATE
      gl.domElement.style.cursor = ativa ? 'grab' : ''
    }
    const baixar = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        teclas.current.add('Shift')
        return
      }
      if (e.code === 'Space' && !e.ctrlKey && !e.metaKey && !e.altKey && !digitando(e.target) && !emControle(e.target)) {
        e.preventDefault()
        if (!estadoNavegacao.espaco) maoAtiva(true)
        return
      }
      const andar = tecla(e)
      if (!ANDAR.has(andar) || e.ctrlKey || e.metaKey || e.altKey || digitando(e.target)) return
      teclas.current.add(andar)
      e.preventDefault()
    }
    const soltar = (e: KeyboardEvent) => {
      if (e.code === 'Space' && estadoNavegacao.espaco) maoAtiva(false)
      teclas.current.delete(e.key)
      const andar = TECLAS_DE_ANDAR[e.key.toLowerCase()]
      if (andar) teclas.current.delete(andar)
    }
    const limpar = () => {
      teclas.current.clear()
      if (estadoNavegacao.espaco) maoAtiva(false)
      estadoNavegacao.botaoDireito = false
    }
    window.addEventListener('keydown', baixar)
    window.addEventListener('keyup', soltar)
    window.addEventListener('blur', limpar)
    return () => {
      window.removeEventListener('keydown', baixar)
      window.removeEventListener('keyup', soltar)
      window.removeEventListener('blur', limpar)
      if (estadoNavegacao.espaco) maoAtiva(false)
    }
  }, [controles, gl])

  const passo = useRef(new Vector3())
  const frente = useRef(new Vector3())
  const lado = useRef(new Vector3())

  useFrame((_, dt) => {
    if (!controles) return
    const f = foco.current
    if (f) {
      const t = Math.min(1, (performance.now() - f.inicio) / DURACAO_FOCO_MS)
      const k = suavizar(t)
      camera.position.lerpVectors(f.de.posicao, f.para.posicao, k)
      controles.target.lerpVectors(f.de.alvo, f.para.alvo, k)
      controles.update()
      if (t >= 1) foco.current = null
    }

    const t = teclas.current
    if (t.size === 0 || controles.enabled === false) return
    const v = velocidadeAndar * (t.has('Shift') ? 3 : 1) * Math.min(dt, 0.1)
    // a frente é para onde se olha, no plano do chão; o lado, à direita dela
    camera.getWorldDirection(frente.current)
    frente.current.y = 0
    if (frente.current.lengthSq() < 1e-9) frente.current.set(0, 0, -1)
    frente.current.normalize()
    lado.current.crossVectors(frente.current, camera.up).normalize()
    passo.current.set(0, 0, 0)
    if (t.has('ArrowUp')) passo.current.add(frente.current)
    if (t.has('ArrowDown')) passo.current.sub(frente.current)
    if (t.has('ArrowRight')) passo.current.add(lado.current)
    if (t.has('ArrowLeft')) passo.current.sub(lado.current)
    if (t.has('PageUp')) passo.current.y += 1
    if (t.has('PageDown')) passo.current.y -= 1
    if (passo.current.lengthSq() === 0) return
    passo.current.normalize().multiplyScalar(v)
    foco.current = null
    camera.position.add(passo.current)
    controles.target.add(passo.current)
    controles.update()
  })

  return null
}
